import { ref, watch, computed, onMounted, onBeforeMount } from 'vue';
import { useRouter } from 'vue-router';
import { toastResponseInSetup } from '@/common/handleServerResponse';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';

export function useFormRouter(componentIdentifier) {
  const store = useStore();
  const router = useRouter();
  const toast = useToast();
  const steps = [...(store.state.formRoutes?.steps ?? [])];
  const componentStep = computed(() =>
    steps.find((step) => step?.identifier == componentIdentifier)
  );
  const currentStepIndex = computed(
    () => store.state.formRoutes?.activeStepIndex
  );
  const currentStep = computed(() => store.state.formRoutes?.activeStep);
  const currentStage = computed(
    () => store.state.onboardingPacket?.current_stage || 1
  );
  const isDisabled = computed(
    () => componentIdentifier != currentStep.value?.identifier
  );
  const initialRoute = computed(() => store.state.formRoutes?.initialHit);
  const isInitialRoute = computed(
    () => store.state.formRoutes?.initialHit === null
  );
  const localUpdatedStepIndex = ref(null);
  const localInitialHit = ref(false);
  const userCompanyType = computed(
    () => store.state.users.currentUser?.company?.company_type
  );
  const stepDirection = computed(
    () => store.state.users.formRoutes?.stepDirection
  );

  const hasAvailableCarrierElectedClassifications = computed(
    () =>
      store.state.carriers?.availableCarrierElectedClassifications?.length > 0
  );
  const availableCarrierElectedClassificationsHaveLoaded = computed(
    () => store.state.carriers?.availableCarrierElectedClassificationsHaveLoaded
  );
  const isInitialRedirectStep = computed(
    () => componentIdentifier == 'Classifications'
  );
  const debugPrefix = componentIdentifier + ' > formRouter: ';

  const onboarding_id = computed(() => store.state.carriers?.carrierData?.id);
  const firstTime = computed(() => {
    const localFirstTimeIdentifier = steps.filter(
      (step) => step?.identifier == componentIdentifier
    )?.[0]?.firstTimeIdentifier;
    if (localFirstTimeIdentifier?.length > 0) {
      return store.state.firstTime?.[localFirstTimeIdentifier];
    } else {
      return true;
    }
  });

  onBeforeMount(async () => {
    if (isDisabled.value) return;
    if (availableCarrierElectedClassificationsHaveLoaded.value) return;
    store.commit(
      'addFormDebugMessage',
      debugPrefix +
        'onBeforeMount, checking for carrier elected classifications'
    );
    await store.dispatch('carriers/getAvailableCarrierElectedClassifications');
  });

  const shouldShowCarrierElectedClassifications = async () => {
    let hasViewedCarrierElection = [];
    let hasViewedCarrierElectionLS = localStorage.getItem(
      'has_viewed_carrier_election'
    );
    if (hasViewedCarrierElectionLS?.length > 0) {
      hasViewedCarrierElection = JSON.parse(hasViewedCarrierElectionLS);
      if (hasViewedCarrierElection.includes(onboarding_id.value)) {
        return false;
      }
    }
    hasViewedCarrierElection.push(onboarding_id.value);
    localStorage.setItem(
      'has_viewed_carrier_election',
      JSON.stringify(hasViewedCarrierElection)
    );
    return true;
  };

  const moveToNextStep = async () => {
    store.commit('addFormDebugMessage', debugPrefix + 'moveToNextStep called');
    if (isDisabled.value) {
      store.commit(
        'addFormDebugMessage',
        'ERROR | ' + debugPrefix + 'Nav disabled, returning'
      );
      return;
    }
    console.log('currentStepIndex.value', currentStepIndex.value);
    console.log('steps.length', steps.length);
    console.log('localUpdatedStepIndex.value', localUpdatedStepIndex.value);
    if (currentStepIndex.value < steps.length - 1) {
      localUpdatedStepIndex.value = currentStepIndex.value + 1;
    }
    console.log(
      'localUpdatedStepIndex.value + 1?',
      localUpdatedStepIndex.value
    );
  };

  const moveToPreviousStep = async () => {
    store.commit(
      'addFormDebugMessage',
      debugPrefix + "'moveToPreviousStep' called"
    );
    if (isDisabled.value)
      return store.commit(
        'addFormDebugMessage',
        'ERROR | ' + debugPrefix + 'Nav disabled, returning'
      );
    // console.log('currentStepIndex.value', currentStepIndex.value);
    // console.log('steps.length', steps.length);
    // console.log('localUpdatedStepIndex.value', localUpdatedStepIndex.value);
    if (currentStepIndex.value > 0) {
      localUpdatedStepIndex.value = currentStepIndex.value - 1;
    }
    console.log(
      'localUpdatedStepIndex.value - 1?',
      localUpdatedStepIndex.value
    );
  };

  const moveToStepByIdentifier = async (identifier) => {
    store.commit(
      'addFormDebugMessage',
      debugPrefix + 'moveToStepByIdentifier (' + identifier + ') called'
    );
    if (isDisabled.value)
      return store.commit(
        'addFormDebugMessage',
        'ERROR | ' + debugPrefix + 'Nav disabled, returning'
      );
    if (identifier == 'AttemptToComplete') {
      if (currentStage.value === 9) {
        return executeContractAndCompletePacket();
      } else if (currentStage.value === 8 && currentStep.value?.step === 9) {
        // Conditional for when the onboarding packet on the frontend is in stage 9 but the server
        // is in stage 8, attempt to auto-update the stage and then executeContractAndCompletePacket()
        store.commit(
          'addFormDebugMessage',
          'WARNING | ' +
            debugPrefix +
            "Attempting to complete packet from step 9 but server is still on stage 8, will attempt 'nextStepPatch'"
        );
        const nextStepRes = await store.dispatch(
          'onboardingPacket/nextStepPatch',
          { id: onboarding_id.value }
        );
        if (nextStepRes?.data?.stage == 9) {
          store.commit(
            'addFormDebugMessage',
            debugPrefix +
              "'nextStepPatch' complete, server is now on stage " +
              nextStepRes?.data?.stage
          );
          return executeContractAndCompletePacket();
        } else {
          return store.commit(
            'addFormDebugMessage',
            debugPrefix +
              "'nextStepPatch' complete, but server is now on stage " +
              nextStepRes?.data?.stage +
              ' and is unable to complete packet, returning'
          );
        }
      } else {
        return store.commit(
          'addFormDebugMessage',
          'WARNING | ' +
            debugPrefix +
            'Possible tampering, attempting to complete packet but server is only on stage ' +
            currentStage.value +
            ', returning'
        );
      }
    }
    const stepIndex = steps.findIndex((step) => step.identifier === identifier);
    if (stepIndex >= 0 && stepIndex < steps.length) {
      localUpdatedStepIndex.value = stepIndex;
    }
  };

  const moveToCurrentStageStep = async () => {
    store.commit(
      'addFormDebugMessage',
      debugPrefix + "'moveToCurrentStageStep' called"
    );
    if (isDisabled.value && !localInitialHit.value)
      return store.commit(
        'addFormDebugMessage',
        'ERROR | ' + debugPrefix + 'Nav disabled, returning'
      );

    let currentStageStepIndex = steps.findIndex(
      (step) => step?.step === currentStage.value
    );

    // Check if we should skip stage 9 (Contract) due to auto_skip_stage_nine
    // 11106 and 15244 are the company IDs for Uber Freight.
    // This is a temporary fix—we plan to enable this dynamically for different
    // onboarding packet issuers.
    const issuerId = store.state.carriers?.carrierData.connection?.issuer?.id;
    const isStaging = window.location.hostname.includes('staging.');
    const isUberFreight = isStaging ? issuerId === 11106 : issuerId === 15244;

    if (
      currentStage.value === 9 &&
      store.state.carriers?.carrierData?.auto_skip_stage_nine &&
      store.state.users.featureFlags?.skip_execute_contract?.enabled &&
      isUberFreight
    ) {
      store.commit(
        'addFormDebugMessage',
        debugPrefix +
          'Auto skip stage nine is true, attempting to complete packet instead of navigating to Contract-1'
      );
      return moveToStepByIdentifier('AttemptToComplete');
    }

    if (localInitialHit.value) {
      store.commit(
        'addFormDebugMessage',
        debugPrefix +
          'Beginning at ' +
          steps?.[currentStageStepIndex]?.identifier
      );
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Initial server stage is ' + currentStage.value
      );
    }
    if (currentStepIndex.value < steps.length - 1) {
      localUpdatedStepIndex.value = currentStageStepIndex;
    }
  };

  const updateUrlPath = () => {
    if (localInitialHit.value == true) {
      console.log('localInitialHit is true');
      localInitialHit.value = false;
    } else {
      store.commit(
        'addFormDebugMessage',
        debugPrefix +
          'Moved from ' +
          componentIdentifier +
          ' to ' +
          currentStep.value?.identifier
      );
    }
    const path = '#' + currentStep.value?.identifier?.toLowerCase();
    // const path = '#' + currentStepIdentifier.value?.toLowerCase();
    window.history.pushState(null, null, path);
    store.commit('addFormDebugMessage', debugPrefix + 'Updated url to ' + path);
  };

  const prepareStepTransition = async (newValue, oldValue) => {
    if (isDisabled.value && !isInitialRedirectStep.value) return;
    // store.commit('addFormDebugMessage', debugPrefix+'Preparing step transition: '+oldValue+' to '+newValue);
    if (
      localInitialHit.value ||
      steps?.[newValue]?.step == steps?.[oldValue]?.step
    ) {
      await store.dispatch('formRoutes/updateStepTransition', 'default');
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Updated step transition: default'
      );
    } else if (steps?.[newValue]?.step > steps?.[oldValue]?.step) {
      await store.dispatch('formRoutes/updateStepTransition', 'forward');
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Updated step transition: forward'
      );
    } else {
      await store.dispatch('formRoutes/updateStepTransition', 'back');
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Updated step transition: back'
      );
    }
  };

  const shouldHitServerNext = (updatedStepIndex, priorStepIndex) => {
    return (
      steps?.[updatedStepIndex]?.step &&
      steps?.[priorStepIndex]?.step &&
      steps?.[updatedStepIndex]?.step > steps?.[priorStepIndex]?.step &&
      steps?.[updatedStepIndex]?.step > currentStage.value
    );
  };

  const shouldSetPriorStepFirstTimeToFalse = (
    updatedStepIndex,
    priorStepIndex
  ) => {
    return (
      steps?.[updatedStepIndex]?.step &&
      steps?.[priorStepIndex]?.step &&
      steps?.[updatedStepIndex]?.step > steps?.[priorStepIndex]?.step
    );
  };

  const handlePacketCompletion = async (response) => {
    if (response?.data?.status == 'incomplete') return;
    if ([200, 201].includes(response?.status)) {
      // If the packet is complete then we need to move on.  This could be because
      // the issuer doesn't require some of the final steps.
      if (response.data?.status == 'complete') {
        store.commit(
          'addFormDebugMessage',
          `PACKET COMPLETE | ${debugPrefix}Packet is complete`
        );
        let redirectUrl = null;
        switch (userCompanyType.value) {
          case 'dispatcher':
            store.commit(
              'addFormDebugMessage',
              debugPrefix +
                'Dispatcher completed packet; redirecting to Dispatcher Dashboard'
            );
            return router.push({ name: 'Dispatcher Dashboard' });
          case 'broker':
          case 'carrier':
            redirectUrl = getRedirectUrlAfterSignIn();
            if (redirectUrl) {
              if (typeof redirectUrl === 'object') {
                return router.push(redirectUrl);
              } else {
                window.location.href = redirectUrl;
              }
            } else {
              return router.push({
                name: 'Carrier Onboarding Packet Status',
                params: { id: onboarding_id.value },
              });
            }
        }
      } else if (response.data?.status == 'held_for_carrier_approval') {
        store.commit(
          'addFormDebugMessage',
          `PACKET HELD | ${debugPrefix}Held for carrier approval, redirecting to Dispatcher Dashboard`
        );
        return router.push({ name: 'Dispatcher Dashboard' });
      } else if (
        [
          'held_for_failed_rules_assessment',
          'held_for_incomplete_rules_assessment',
        ].includes(response.data?.status)
      ) {
        store.commit(
          'addFormDebugMessage',
          `PACKET HELD | ${debugPrefix}Packet ${response.data?.status}`
        );
        switch (userCompanyType.value) {
          case 'dispatcher':
            store.commit(
              'addFormDebugMessage',
              `REDIRECT | ${debugPrefix}Redirecting to Dispatcher Dashboard`
            );
            return router.push({
              name: 'Dispatcher Onboarding Packet Status',
              params: { id: onboarding_id.value },
            });
          case 'broker':
          case 'carrier':
            store.commit(
              'addFormDebugMessage',
              `REDIRECT | ${debugPrefix}Redirecting to Carrier Dashboard`
            );
            return router.push({
              name: 'Carrier Onboarding Packet Status',
              params: { id: onboarding_id.value },
            });
        }
      }
    } else {
      toastResponseInSetup(this, response, null);
    }
  };

  const getRedirectUrlAfterSignIn = () => {
    let redirectAfterSignIn;
    let inLocalStorage = false;
    try {
      redirectAfterSignIn = JSON.parse(
        localStorage.getItem('redirect_url_after_sigin')
      );
      inLocalStorage = true;
    } catch {
      redirectAfterSignIn =
        store.state.brokers?.localStorage?.redirect_url_after_sigin;
    }

    if (redirectAfterSignIn) {
      if (inLocalStorage) {
        localStorage.removeItem('redirect_url_after_sigin');
      }
      store.commit(
        'addFormDebugMessage',
        `REDIRECT | ${debugPrefix}Redirecting to redirect_url_after_sigin`
      );
      return redirectAfterSignIn;
    } else {
      store.commit(
        'addFormDebugMessage',
        `REDIRECT | ${debugPrefix}redirect_url_after_sigin is not present`
      );
      return false;
    }
  };

  const updatePacket = async (updatedStepIndex, priorStepIndex) => {
    const canHitNextStep = shouldHitServerNext(
      updatedStepIndex,
      priorStepIndex
    );
    store.commit(
      'addFormDebugMessage',
      debugPrefix +
        "'updatePacket' called, shouldHitServerNext = " +
        canHitNextStep
    );
    // Find request payload body -- if exists submit update to onboarding packet
    if (
      Object.entries(store.state.onboardingPacket.onboardingPacketBody)
        ?.length > 0 &&
      updatedStepIndex > priorStepIndex
    ) {
      store.commit(
        'addFormDebugMessage',
        `PATCH WITH DATA | (Stage ${currentStage.value}) | ${debugPrefix}Updates to onboarding packet present; submitting to server`
      );
      const res = await store.dispatch('onboardingPacket/updatePacket', {
        id: onboarding_id.value,
        body: { ...store.state.onboardingPacket.onboardingPacketBody },
        canHitNextStep: canHitNextStep,
      });
      store.commit(
        'addFormDebugMessage',
        `SERVER RESPONSE | STAGE ${res?.data?.stage} | ${debugPrefix}PATCH WITH DATA call returned ${res?.status}`
      );
      if (
        [200, 201, 204].includes(res?.status) &&
        shouldSetPriorStepFirstTimeToFalse(updatedStepIndex, priorStepIndex)
      ) {
        store.commit(
          'addFormDebugMessage',
          `${debugPrefix}Setting prior step firstTime value to false (key: '${steps?.[priorStepIndex]?.firstTimeIdentifier}')`
        );
        store.commit('firstTime/updateStepFirstTime', {
          step: steps?.[priorStepIndex]?.firstTimeIdentifier,
          value: false,
        });
      }
      handlePacketCompletion(res);
      return res;
    } else if (canHitNextStep) {
      store.commit(
        'addFormDebugMessage',
        `NEXT WITHOUT DATA | (Stage ${currentStage.value} to ${
          currentStage.value + 1
        }) | ${debugPrefix}No updates to onboarding packet present and SHOULD hit next step; submitting to server`
      );
      // If no change was made to the onboarding packet, the next step should hit if available
      const nextStepRes = await store.dispatch(
        'onboardingPacket/nextStepPatch',
        { id: onboarding_id }
      );
      store.commit(
        'addFormDebugMessage',
        `SERVER RESPONSE | STAGE ${nextStepRes?.data?.stage} | ${debugPrefix}NEXT WITHOUT DATA call returned ${nextStepRes?.status}`
      );
      if (
        [200, 201, 204].includes(nextStepRes?.status) &&
        shouldSetPriorStepFirstTimeToFalse(updatedStepIndex, priorStepIndex)
      ) {
        store.commit(
          'addFormDebugMessage',
          `${debugPrefix}Setting prior step firstTime value to false (key: '${steps?.[priorStepIndex]?.firstTimeIdentifier}')`
        );
        store.commit('firstTime/updateStepFirstTime', {
          step: steps?.[priorStepIndex]?.firstTimeIdentifier,
          value: false,
        });
      }
      handlePacketCompletion(nextStepRes);
      return nextStepRes;
    } else {
      store.commit(
        'addFormDebugMessage',
        `BYPASSING NEXT | ${debugPrefix}No updates to onboarding packet and do not need to hit next step`
      );
      // not hitting next step but need to transition to next sub step
      return { status: 200 };
    }
  };

  const updateActiveStep = async (updatedStepIndex, priorStepIndex) => {
    if (isDisabled.value && !localInitialHit.value) {
      store.commit(
        'addFormDebugMessage',
        'ERROR: ' +
          debugPrefix +
          'updateActiveStep called but disabled, returning'
      );
      return;
    }
    if (priorStepIndex > updatedStepIndex) {
      checkForClassificationsNextIdentifier(updatedStepIndex, priorStepIndex);
    }
    if (!localInitialHit.value) {
      if (stepDirection.value !== 'back') {
        store.commit(
          'addFormDebugMessage',
          'ATTENTION | ' +
            debugPrefix +
            'stepDirection is back, updating packet if data present but should not hit nextStepPatch'
        );
      }
      let updateRes = await updatePacket(updatedStepIndex, priorStepIndex);
      if (updateRes) {
        if ([422].includes(updateRes?.status)) {
          store.commit(
            'addFormDebugMessage',
            'ATTENTION | SERVER | ' +
              debugPrefix +
              'nextStepPatch returned 422, should not continue to next step'
          );
          store.commit('formRoutes/setErrorOnNextStepPatch', true);
          return updateRes;
        } else {
          store.commit(
            'addFormDebugMessage',
            'SERVER | ' +
              debugPrefix +
              'nextStepPatch returned ' +
              updateRes?.status
          );
        }
      }
      // if (stepDirection.value !== 'back') {
      //     await updatePacket(updatedStepIndex, priorStepIndex);
      // } else {
      //     store.commit('addFormDebugMessage', debugPrefix+'Skipping updatePacket because stepDirection is back');
      // }
    }
    await prepareStepTransition(updatedStepIndex, priorStepIndex);
    await store.dispatch(
      'formRoutes/updateActiveStepIdentifier',
      steps?.[updatedStepIndex]?.identifier
    );
    if (updatedStepIndex != currentStepIndex.value) {
      await store.dispatch(
        'formRoutes/updateActiveStepIndex',
        updatedStepIndex
      );
    }
    // Run this last to trigger the form step change
    await store.dispatch(
      'formRoutes/updateActiveStep',
      steps?.[updatedStepIndex]
    );
    updateUrlPath();
    localUpdatedStepIndex.value = null;
  };

  watch(localUpdatedStepIndex, async (newValue, oldValue) => {
    console.log(
      'localUpdatedStepIndex changed to ' + newValue + ' from ' + oldValue
    );
    if (isDisabled.value && !localInitialHit.value) return;
    if (newValue == null) return;
    if (newValue < currentStepIndex.value) {
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Setting stepDirection to back'
      );
      store.commit('formRoutes/setStepDirection', 'back');
    } else {
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Setting stepDirection to forward'
      );
      store.commit('formRoutes/setStepDirection', 'forward');
    }
    let initialText = localInitialHit.value ? '(Initial Default)' : '';
    store.commit(
      'addFormDebugMessage',
      debugPrefix +
        'Step Index changed from ' +
        currentStepIndex.value +
        ' ' +
        initialText +
        ' to ' +
        newValue
    );
    store.commit(
      'addFormDebugMessage',
      debugPrefix +
        "Running 'updateActiveStep' from " +
        currentStep.value?.identifier +
        ' to ' +
        steps?.[newValue]?.identifier
    );
    let updateActiveStepRes = await updateActiveStep(
      newValue,
      currentStepIndex.value
    );
    if (updateActiveStepRes) {
      if ([422].includes(updateActiveStepRes?.status)) {
        store.commit(
          'addFormDebugMessage',
          debugPrefix +
            'Displaying error message from nextStepPatch 422 response, should not continue to next step'
        );
        toastResponseInSetup(toast, updateActiveStepRes, null);
      }
    }
  });

  watch(
    currentStep,
    async (newValue) => {
      let comparePath = '#' + newValue?.identifier?.toLowerCase();
      if (window.location.hash.toLowerCase() != comparePath) updateUrlPath();
    },
    { immediate: true, deep: true }
  );

  watch(isDisabled, async (newValue) => {
    if (newValue == true) {
      store.commit('addFormDebugMessage', debugPrefix + 'Nav disabled');
    } else if (newValue == false) {
      store.commit('addFormDebugMessage', debugPrefix + 'Nav enabled');
    } else {
      store.commit(
        'addFormDebugMessage',
        'ERROR | ' +
          debugPrefix +
          'isDisabled.value (' +
          newValue +
          ') is neither true nor false'
      );
    }
  });

  onMounted(async () => {
    if (isDisabled.value && !isInitialRedirectStep.value) return;
    store.commit(
      'addFormDebugMessage',
      debugPrefix + '(onMounted) Nav enabled'
    );
    if (isInitialRoute.value) {
      localInitialHit.value = true;
    }
    if (localInitialHit.value && currentStage.value > 0) {
      let currentStageStepIndex = steps.findIndex(
        (step) => step?.step === currentStage.value
      );
      await store.dispatch(
        'formRoutes/markInitialHit',
        steps?.[currentStageStepIndex]?.identifier
      );
      if (hasAvailableCarrierElectedClassifications.value) {
        store.commit(
          'addFormDebugMessage',
          debugPrefix +
            '(onMounted) Has carrier-elected classifications available'
        );
        if (await shouldShowCarrierElectedClassifications()) {
          store.commit(
            'addFormDebugMessage',
            debugPrefix + '(onMounted) Beginning at Classifications'
          );
          store.commit(
            'addFormDebugMessage',
            debugPrefix +
              '(onMounted) Initial server stage is ' +
              currentStage.value
          );
          localUpdatedStepIndex.value = 0;
        } else {
          store.commit(
            'addFormDebugMessage',
            debugPrefix +
              '(onMounted) Has previously viewed carrier-elected classifications'
          );
          moveToCurrentStageStep();
        }
      } else {
        store.commit(
          'addFormDebugMessage',
          debugPrefix +
            '(onMounted) No carrier-elected classifications available'
        );
        moveToCurrentStageStep();
      }
    }
  });

  const checkForClassificationsNextIdentifier = async (
    updatedStepIndex,
    priorStepIndex
  ) => {
    if (updatedStepIndex == 0) {
      const classificationsNextIdentifier = steps?.[priorStepIndex]?.identifier;
      store.commit(
        'addFormDebugMessage',
        debugPrefix +
          'Setting classificationsNextIdentifier to ' +
          classificationsNextIdentifier
      );
      store.commit(
        'formRoutes/setClassificationsNextIdentifier',
        classificationsNextIdentifier
      );
      return;
    }
  };

  const editActiveStep = async () => {
    const localFirstTimeIdentifier = steps.filter(
      (step) => step?.identifier == componentIdentifier
    )?.[0]?.firstTimeIdentifier;
    store.commit('firstTime/updateStepFirstTime', {
      step: localFirstTimeIdentifier,
      value: true,
    });
  };

  // Provide a way to manually redirect to Classifications step (used by HamburgerMenu link)
  const redirectToClassifications = computed(
    () => store.state.formRoutes.redirectToClassifications
  );
  watch(redirectToClassifications, async (newValue) => {
    if (newValue && !isDisabled.value) {
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Redirecting to Classifications'
      );
      store.commit(
        'addFormDebugMessage',
        debugPrefix +
          'Setting classificationsNextIdentifier to ' +
          componentIdentifier
      );
      store.commit(
        'formRoutes/setClassificationsNextIdentifier',
        componentIdentifier
      );
      await store.dispatch('formRoutes/updateStepTransition', 'back');
      store.commit(
        'addFormDebugMessage',
        debugPrefix + 'Updated step transition: back'
      );
      await store.dispatch(
        'formRoutes/updateActiveStepIdentifier',
        'Classifications'
      );
      await store.dispatch('formRoutes/updateActiveStepIndex', 0);
      updateUrlPath();
      // Run this last to trigger the form step change
      await store.dispatch('formRoutes/updateActiveStep', steps?.[0]);
      // Reset the redirectToClassifications flag to false
      store.commit('formRoutes/setRedirectToClassifications', false);
    }
  });

  // Execute the contract for the onboarding packet and complete the packet.  Handle the logic for
  // directing the user to the next step depending on the company type and the status of the packet
  const executeContractAndCompletePacket = async () => {
    // 11106 and 15244 are the company IDs for Uber Freight.
    // This is a temporary fix—we plan to enable this dynamically for different
    // onboarding packet issuers.
    const issuerId = store.state.carriers?.carrierData.connection?.issuer?.id;
    const isStaging = window.location.hostname.includes('staging.');
    const isUberFreight = isStaging ? issuerId === 11106 : issuerId === 15244;

    if (
      store.state.carriers.carrierData.auto_skip_stage_nine &&
      store.state.users.featureFlags?.skip_execute_contract?.enabled &&
      isUberFreight
    ) {
      handlePacketCompletion({
        data: {
          status: store.state.carriers.carrierData.status,
        },
      });
      return;
    }

    const executeRes = await store.dispatch('onboardingPacket/executeContract');
    if ([200, 201].includes(executeRes?.status)) {
      store.commit(
        'addFormDebugMessage',
        'EXECUTED CONTRACT | ' + debugPrefix + 'Server executed contract(s)'
      );
      handlePacketCompletion(executeRes);
    } else {
      store.commit(
        'addFormDebugMessage',
        'ERROR | ' +
          debugPrefix +
          'Server failed to execute contract, status: ' +
          executeRes?.status
      );
      toastResponseInSetup(toast, executeRes, null);
    }
  };

  return {
    moveToNextStep,
    moveToPreviousStep,
    moveToStepByIdentifier,
    moveToCurrentStageStep,
    isDisabled,
    initialRoute,
    componentStep,
    editActiveStep,
    firstTime,
  };
}
